<template>
    <div class="form-group password-input">
        <label v-if="label" :for="inputId" :class="{'sr-only': srOnly}">
            <slot name="label">{{ label }}</slot>
        </label>
        <div class="input-group" :class="{ 'is-invalid': hasError }">
            <input
                ref="input"
                :type="revealed ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': hasError }"
                :id="inputId"
                :name="name"
                :value="value"
                :placeholder="placeholder"
                :aria-label="label || placeholder"
                :aria-invalid="hasError ? 'true' : null"
                :aria-describedby="describedBy"
                :autocomplete="autocomplete"
                :disabled="disabled"
                :required="required"
                :maxlength="maxlength"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                @input="$emit('input', $event.target.value)"
                @keyup="checkCapsLock"
                @keydown="checkCapsLock"
                @keyup.enter="$emit('enter')"
                @focus="$emit('focus', $event)"
                @blur="onBlur"
            />
            <div v-if="toggle" class="input-group-append">
                <button
                    type="button"
                    class="btn btn-outline-secondary password-toggle"
                    :aria-label="revealed ? $t('hide-password') : $t('show-password')"
                    :title="revealed ? $t('hide-password') : $t('show-password')"
                    :aria-pressed="revealed ? 'true' : 'false'"
                    :aria-controls="inputId"
                    :disabled="disabled"
                    @click="toggleReveal"
                >
                    <b-icon :icon="revealed ? 'eye-slash' : 'eye'" aria-hidden="true"></b-icon>
                </button>
            </div>
        </div>

        <small v-if="capsLockOn" :id="`${inputId}-caps`" class="d-block text-warning mt-1" role="status">
            <b-icon icon="exclamation-triangle-fill" aria-hidden="true"></b-icon>
            {{ $t('caps-lock-on') }}
        </small>

        <div v-if="hasError && errorList.length" :id="`${inputId}-error`" class="invalid-feedback d-block" role="alert">
            <div v-for="(msg, i) in errorList" :key="i">{{ msg }}</div>
        </div>

        <div v-if="showStrength && value" :id="`${inputId}-strength`" class="mt-1">
            <div class="progress password-strength">
                <div
                    class="progress-bar"
                    :class="strength.variant"
                    role="progressbar"
                    :style="{ width: `${strength.score * 25}%` }"
                    :aria-valuenow="strength.score"
                    aria-valuemin="0"
                    aria-valuemax="4"
                    :aria-label="$t('password-strength')"
                ></div>
            </div>
            <small class="text-muted">{{ $t('password-strength') }}: {{ $t(strength.label) }}</small>
        </div>

        <small v-if="description || $slots.description" :id="`${inputId}-description`" class="form-text text-muted">
            <slot name="description">{{ description }}</slot>
        </small>
    </div>
</template>

<script>
import uuid from 'src/modules/uuid.js'

const STRENGTH_LEVELS = [
    { label: 'password-strength-weak', variant: 'bg-danger' },
    { label: 'password-strength-weak', variant: 'bg-danger' },
    { label: 'password-strength-fair', variant: 'bg-warning' },
    { label: 'password-strength-good', variant: 'bg-info' },
    { label: 'password-strength-strong', variant: 'bg-success' },
]

export function passwordScore(password) {
    if (!password) return 0
    let score = 0
    if (password.length >= 8) score++
    if (password.length >= 12) score++
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
    if (/\d/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++
    // Very short passwords are always weak, whatever characters they contain
    if (password.length < 8) score = Math.min(score, 1)
    return Math.min(score, 4)
}

export default {
    props: {
        value: String,
        label: String,
        srOnly: {
            type: Boolean,
            default: false,
        },
        id: String,
        name: String,
        placeholder: String,
        // 'current-password' for logins, 'new-password' for creating/changing passwords
        autocomplete: {
            type: String,
            default: 'current-password',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        maxlength: Number,
        // Show the reveal/hide button
        toggle: {
            type: Boolean,
            default: true,
        },
        // Hide the password again when the field loses focus
        hideOnBlur: {
            type: Boolean,
            default: false,
        },
        capsLockWarning: {
            type: Boolean,
            default: true,
        },
        showStrength: {
            type: Boolean,
            default: false,
        },
        description: String,
        error: {
            type: Boolean,
            default: false,
        },
        // A single message or a list of messages. A non-empty message also marks the field as invalid.
        errorMessage: {
            type: [String, Array],
            default: '',
        },
    },
    data() {
        return {
            uuid: new uuid(),
            revealed: false,
            capsLockOn: false,
        }
    },
    computed: {
        inputId() {
            return this.id || this.uuid.ID()
        },
        errorList() {
            const messages = Array.isArray(this.errorMessage) ? this.errorMessage : [this.errorMessage]
            return messages.filter(Boolean)
        },
        hasError() {
            return this.error || this.errorList.length > 0
        },
        strength() {
            const score = passwordScore(this.value)
            return { score, ...STRENGTH_LEVELS[score] }
        },
        describedBy() {
            const ids = []
            if (this.capsLockOn) ids.push(`${this.inputId}-caps`)
            if (this.hasError && this.errorList.length) ids.push(`${this.inputId}-error`)
            if (this.showStrength && this.value) ids.push(`${this.inputId}-strength`)
            if (this.description || this.$slots.description) ids.push(`${this.inputId}-description`)
            return ids.length ? ids.join(' ') : null
        },
    },
    watch: {
        value(newValue) {
            // Re-mask once the field is cleared, e.g. after a successful submit
            if (!newValue) this.revealed = false
        },
    },
    methods: {
        toggleReveal() {
            this.revealed = !this.revealed
            // Keep the cursor in the field so the user can carry on typing
            this.$nextTick(() => this.focus())
        },
        checkCapsLock(event) {
            if (!this.capsLockWarning || typeof event.getModifierState !== 'function') return
            this.capsLockOn = event.getModifierState('CapsLock')
        },
        onBlur(event) {
            this.capsLockOn = false
            if (this.hideOnBlur && !this.$el.contains(event.relatedTarget)) this.revealed = false
            this.$emit('blur', event)
        },
        focus() {
            this.$refs.input && this.$refs.input.focus()
        },
    },
}
</script>

<style scoped>
.password-strength {
    height: 4px;
}
</style>
